import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoginModal from './LoginModal';

/**
 * ProtectedRoute
 *
 * Wraps any route that requires authentication.
 *
 * Behaviour:
 *  - Auth loading  → show a full-page spinner (prevents flash-of-broken-content)
 *  - Not logged in → dim/blur the page behind a LoginModal; the URL stays intact
 *                    so after login the user lands exactly where they wanted
 *  - Logged in     → render children normally
 */
export default function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, userProfile } = useAuth();
  const [showModal, setShowModal] = useState(false);

  // As soon as we know the user is NOT authenticated, show the modal
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setShowModal(true);
    } else if (isAuthenticated) {
      setShowModal(false);
    }
  }, [isLoading, isAuthenticated]);

  // ── Loading state ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        gap: '20px',
      }}>
        {/* Spinner */}
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          border: '3px solid #e2e8f0',
          borderTopColor: '#6366f1',
          animation: 'spin 0.75s linear infinite',
        }} />
        <p style={{ fontSize: '14px', color: '#94a3b8', fontWeight: 500 }}>
          Loading…
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Not authenticated ─────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <>
        {/* Blurred, dimmed page content underneath */}
        <div style={{
          minHeight: '100vh',
          filter: 'blur(8px)',
          opacity: 0.35,
          pointerEvents: 'none',
          userSelect: 'none',
          overflow: 'hidden',
        }}>
          {children}
        </div>

        {/* Lock icon + message layer */}
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 998,
          gap: '12px',
          pointerEvents: 'none',
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #6366f1, #a855f7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 32px rgba(99,102,241,0.35)',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <p style={{
            fontSize: '15px',
            fontWeight: 600,
            color: '#1e293b',
            background: 'rgba(255,255,255,0.9)',
            padding: '8px 20px',
            borderRadius: '100px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.6)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          }}>
            Sign in to continue
          </p>
        </div>

        {/* Login modal - cannot be dismissed until signed in */}
        <LoginModal
          isOpen={showModal}
          onClose={() => {
            // Allow closing the modal (user can go back to home)
            window.history.back();
          }}
        />
      </>
    );
  }

  // ── Authenticated ─────────────────────────────────────────────────────────
  if (isAuthenticated && userProfile && userProfile.profile_completed === false) {
    if (window.location.pathname !== '/dashboard') {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}
